import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EigooMark from "../components/EigooMark";
import AutonomousSlots from "../components/AutonomousSlots";
import LifecycleTimeline, { PriorityBadge } from "../components/LifecycleTimeline";
import { toast } from "sonner";
import {
    Zap,
    DollarSign,
    Briefcase,
    Award,
    Check,
    Clock,
    PauseCircle,
    PlayCircle,
    LogOut,
    AlertTriangle,
    Sparkles,
    Loader2,
} from "lucide-react";

const currency = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const STATUS_PILL = {
    accepted: { color: "hsl(40 75% 60%)", label: "Accepted" },
    in_progress: { color: "hsl(45 85% 60%)", label: "In Progress" },
    on_hold: { color: "hsl(25 70% 55%)", label: "On Hold" },
    completed: { color: "hsl(153 55% 55%)", label: "Completed" },
};

function StrikeCard({ order, badges, onAccept, accepting, isDirector }) {
    const eligible = isDirector
        || !order.required_skills?.length
        || (order.required_skills || []).some((s) => badges.includes(s));

    return (
        <article
            data-testid={`strike-${order.id}`}
            className="snap-start glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.25)] hover:border-[hsl(45_85%_68%/0.5)] transition-colors flex flex-col gap-4 mb-4"
        >
            <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">{order.category}</div>
                        {order.priority && order.priority !== "standard" && (
                            <PriorityBadge priority={order.priority} />
                        )}
                    </div>
                    <h3 className="font-heading text-2xl tracking-tight mt-1">{order.service_name}</h3>
                    {order.is_custom && order.custom_brief && (
                        <p className="text-sm text-foreground/80 mt-2 leading-relaxed line-clamp-3">{order.custom_brief}</p>
                    )}
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Operative payout</div>
                    <div className="font-heading text-3xl gold-gradient-text mt-1">{currency(order.operative_payout)}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">of {currency(order.amount)} total</div>
                </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 text-xs">
                <div className="flex flex-wrap items-center gap-1.5">
                    {(order.required_skills || []).map((s) => {
                        const has = badges.includes(s);
                        return (
                            <span
                                key={s}
                                className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] border ${
                                    has || isDirector
                                        ? "border-[hsl(153_55%_50%/0.5)] text-[hsl(153_60%_70%)] bg-[hsl(153_55%_15%/0.4)]"
                                        : "border-[hsl(350_60%_45%/0.4)] text-[hsl(350_80%_72%)] bg-[hsl(350_60%_18%/0.3)]"
                                }`}
                            >
                                {s}
                            </span>
                        );
                    })}
                    {!order.required_skills?.length && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] border border-[hsl(40_40%_30%/0.35)] text-muted-foreground">
                            No badge required
                        </span>
                    )}
                </div>
                <div className="text-muted-foreground">
                    Posted {new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
            </div>

            <div className="border-t border-[hsl(40_40%_25%/0.18)] pt-4 flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                    Tracking · <span className="font-mono">{order.tracking_code}</span>
                </div>
                <button
                    onClick={() => onAccept(order)}
                    disabled={!eligible || accepting === order.id}
                    data-testid={`strike-accept-${order.id}`}
                    className={`px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
                        eligible
                            ? "bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] hover:bg-[hsl(45_75%_62%)]"
                            : "bg-[hsl(151_55%_15%)] text-muted-foreground cursor-not-allowed"
                    }`}
                >
                    {accepting === order.id ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                    {eligible ? "Accept Delivery" : "Badge Required"}
                </button>
            </div>
        </article>
    );
}

function ActiveStrikeRow({ order, onStatus, busyId }) {
    const pill = STATUS_PILL[order.status] || STATUS_PILL.accepted;
    return (
        <div className="rounded-xl border border-[hsl(40_40%_25%/0.25)] p-4" data-testid={`active-strike-${order.id}`}>
            <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium truncate">{order.service_name}</div>
                        {order.priority && order.priority !== "standard" && (
                            <PriorityBadge priority={order.priority} />
                        )}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                        {order.category} · <span className="font-mono">{order.tracking_code}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Payout</div>
                    <div className="font-mono text-sm">{currency(order.operative_payout)}</div>
                </div>
                <span
                    className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] border"
                    style={{ borderColor: `${pill.color}55`, color: pill.color }}
                >
                    {pill.label}
                </span>
                <div className="flex items-center gap-1">
                    {order.status !== "in_progress" && (
                        <button
                            data-testid={`status-progress-${order.id}`}
                            onClick={() => onStatus(order.id, "in_progress")}
                            disabled={busyId === order.id}
                            className="p-2 rounded-md hover:bg-[hsl(151_55%_15%)] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                            title="Mark in progress"
                        >
                            <PlayCircle size={16} />
                        </button>
                    )}
                    {order.status !== "on_hold" && (
                        <button
                            data-testid={`status-hold-${order.id}`}
                            onClick={() => onStatus(order.id, "on_hold")}
                            disabled={busyId === order.id}
                            className="p-2 rounded-md hover:bg-[hsl(151_55%_15%)] text-muted-foreground hover:text-[hsl(25_70%_70%)] transition-colors"
                            title="Pause"
                        >
                            <PauseCircle size={16} />
                        </button>
                    )}
                    <button
                        data-testid={`status-complete-${order.id}`}
                        onClick={() => onStatus(order.id, "completed")}
                        disabled={busyId === order.id}
                        className="p-2 rounded-md hover:bg-[hsl(151_55%_15%)] text-muted-foreground hover:text-[hsl(153_55%_60%)] transition-colors"
                        title="Mark complete"
                    >
                        <Check size={16} />
                    </button>
                </div>
            </div>
            {/* 5-stage lifecycle timeline (Director directive 2026.02) */}
            <div className="mt-3 pt-3 border-t border-[hsl(40_40%_25%/0.18)]">
                <LifecycleTimeline stage={order.stage || order.status} compact />
            </div>
        </div>
    );
}

function BadgeManager({ open, badges, available, onClose, onSave }) {
    const [selection, setSelection] = useState(badges);
    const [busy, setBusy] = useState(false);
    useEffect(() => setSelection(badges), [badges, open]);

    if (!open) return null;
    const toggle = (b) => setSelection((cur) => (cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b]));

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
            <div
                className="glass rounded-2xl p-7 max-w-lg w-full border border-[hsl(40_40%_30%/0.4)]"
                onClick={(e) => e.stopPropagation()}
                data-testid="badge-manager"
            >
                <div className="flex items-center gap-2 mb-2">
                    <Award size={18} className="text-[hsl(45_85%_68%)]" />
                    <h2 className="font-heading text-2xl">Operative Badges</h2>
                </div>
                <p className="text-xs text-muted-foreground mb-5">
                    Toggle the badges you hold. The Sovereign Labor Exchange filters your feed to deliveries you can accept.
                </p>
                <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                    {available.map((b) => {
                        const on = selection.includes(b);
                        return (
                            <button
                                key={b}
                                onClick={() => toggle(b)}
                                data-testid={`badge-toggle-${b}`}
                                className={`px-3 py-2 rounded-md text-xs uppercase tracking-[0.2em] border text-left transition-colors ${
                                    on
                                        ? "border-[hsl(45_85%_68%)] bg-[hsl(40_60%_18%/0.45)] text-[hsl(45_85%_68%)]"
                                        : "border-[hsl(40_40%_25%/0.3)] text-muted-foreground hover:border-[hsl(45_85%_68%/0.5)]"
                                }`}
                            >
                                {on ? "✓ " : ""}
                                {b}
                            </button>
                        );
                    })}
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                        Cancel
                    </button>
                    <button
                        disabled={busy}
                        data-testid="badge-save"
                        onClick={async () => {
                            setBusy(true);
                            try {
                                await onSave(selection);
                                onClose();
                            } finally {
                                setBusy(false);
                            }
                        }}
                        className="px-5 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-sm hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                    >
                        {busy ? "Saving..." : "Save badges"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function FieldOperative() {
    const { logout } = useAuth();
    const [feed, setFeed] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [feedLoading, setFeedLoading] = useState(false);
    const [queue, setQueue] = useState(null);
    const [accepting, setAccepting] = useState(null);
    const [statusBusy, setStatusBusy] = useState(null);
    const [badgesOpen, setBadgesOpen] = useState(false);
    const [error, setError] = useState("");
    const sentinelRef = useRef(null);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/foundry/queue");
            setQueue(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed to load");
        }
    }, []);

    const loadFeedPage = useCallback(async (cur) => {
        if (feedLoading) return;
        setFeedLoading(true);
        try {
            const { data } = await api.get("/foundry/feed", { params: { cursor: cur, limit: 8 } });
            setFeed((prev) => (cur ? [...prev, ...data.items] : data.items));
            setCursor(data.next_cursor);
            setHasMore(Boolean(data.next_cursor));
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Feed failed");
        } finally {
            setFeedLoading(false);
        }
    }, [feedLoading]);

    useEffect(() => {
        reload();
        loadFeedPage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Infinite scroll via IntersectionObserver
    useEffect(() => {
        if (!sentinelRef.current || !hasMore) return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && cursor && !feedLoading) {
                    loadFeedPage(cursor);
                }
            },
            { rootMargin: "200px" }
        );
        obs.observe(sentinelRef.current);
        return () => obs.disconnect();
    }, [cursor, hasMore, feedLoading, loadFeedPage]);

    const accept = async (order) => {
        setAccepting(order.id);
        try {
            await api.post(`/foundry/orders/${order.id}/accept`);
            toast.success(`Delivery accepted · ${order.service_name}`);
            setFeed((cur) => cur.filter((o) => o.id !== order.id));
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Accept failed");
        } finally {
            setAccepting(null);
        }
    };

    const updateStatus = async (id, status) => {
        setStatusBusy(id);
        try {
            await api.post(`/foundry/orders/${id}/status`, { status });
            toast.success(`Delivery marked ${status.replace("_", " ")}`);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Status update failed");
        } finally {
            setStatusBusy(null);
        }
    };

    const saveBadges = async (badges) => {
        try {
            const { data } = await api.post("/foundry/operative/badges", { badges });
            toast.success("Badges updated");
            await reload();
            // Reset feed so skill filter applies
            setFeed([]); setCursor(null); setHasMore(true);
            await loadFeedPage(null);
            return data;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
            throw e;
        }
    };

    if (!queue) {
        return <div className="min-h-screen bg-background text-muted-foreground p-10">Calibrating field console...</div>;
    }

    return (
        <div className="min-h-screen bg-background text-foreground grain-overlay relative" data-testid="field-operative">
            <div className="absolute inset-0 pointer-events-none opacity-30">
                <div className="absolute top-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(40_60%_45%/0.10)] blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.22)] blur-[120px]" />
            </div>

            <header className="relative border-b border-[hsl(40_40%_25%/0.2)] bg-[hsl(151_55%_7%/0.7)] backdrop-blur-md sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <EigooMark size="md" sub="Field Operative · Voyaging through the stars" testid="operative-mark" />
                    <div className="flex items-center gap-3 text-sm">
                        <button
                            onClick={() => setBadgesOpen(true)}
                            data-testid="manage-badges"
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                        >
                            <Award size={14} /> Badges ({queue.badges.length})
                        </button>
                        <button
                            onClick={logout}
                            data-testid="operative-logout"
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(350_80%_70%)] transition-colors"
                        >
                            <LogOut size={14} /> Disembark
                        </button>
                    </div>
                </div>
            </header>

            <main className="relative max-w-6xl mx-auto px-6 py-8">
                {/* Autonomous Operative reserved slots — Director's Robotic Dispatch roadmap */}
                <AutonomousSlots />

                <div className="grid lg:grid-cols-3 gap-8">
                {/* LEFT — stats + active strikes */}
                <aside className="space-y-5">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="glass rounded-xl p-4 border border-[hsl(40_40%_25%/0.25)]">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Total Earned</div>
                            <div className="font-heading text-2xl mt-1 gold-gradient-text">{currency(queue.stats.total_earned)}</div>
                        </div>
                        <div className="glass rounded-xl p-4 border border-[hsl(40_40%_25%/0.25)]">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">In Flight</div>
                            <div className="font-heading text-2xl mt-1">{currency(queue.stats.in_flight_value)}</div>
                        </div>
                        <div className="glass rounded-xl p-4 border border-[hsl(40_40%_25%/0.25)]">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Completed</div>
                            <div className="font-heading text-2xl mt-1">{queue.stats.completed_count}</div>
                        </div>
                        <div className="glass rounded-xl p-4 border border-[hsl(40_40%_25%/0.25)]">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Open Strikes</div>
                            <div className="font-heading text-2xl mt-1 text-[hsl(45_85%_68%)]">{queue.stats.open_count}</div>
                        </div>
                    </div>

                    <div className="glass rounded-2xl p-5 border border-[hsl(40_40%_25%/0.25)]">
                        <div className="flex items-center gap-2 mb-3">
                            <Briefcase size={16} className="text-[hsl(45_85%_68%)]" />
                            <h2 className="font-heading text-xl">Active Strikes</h2>
                        </div>
                        {queue.mine.length ? (
                            <div className="space-y-3" data-testid="active-strikes-list">
                                {queue.mine.map((o) => (
                                    <ActiveStrikeRow key={o.id} order={o} onStatus={updateStatus} busyId={statusBusy} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-muted-foreground py-4">No active strikes. Accept one from the feed to begin.</div>
                        )}
                    </div>

                    {queue.completed.length > 0 && (
                        <div className="glass rounded-2xl p-5 border border-[hsl(40_40%_25%/0.25)]">
                            <div className="flex items-center gap-2 mb-3">
                                <DollarSign size={16} className="text-[hsl(153_55%_60%)]" />
                                <h2 className="font-heading text-xl">Recent Payouts</h2>
                            </div>
                            <ul className="space-y-2">
                                {queue.completed.slice(0, 5).map((o) => (
                                    <li key={o.id} className="flex items-center justify-between text-sm border-b border-[hsl(40_40%_25%/0.15)] pb-2">
                                        <span className="truncate flex-1 mr-3">{o.service_name}</span>
                                        <span className="font-mono text-[hsl(153_55%_60%)]">{currency(o.operative_payout)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>

                {/* RIGHT — infinite opportunity feed */}
                <section className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)] mb-1 flex items-center gap-2">
                                <Sparkles size={11} /> Infinite Opportunity Feed
                            </div>
                            <h2 className="font-heading text-3xl tracking-tight">Strikes matched to your badges.</h2>
                        </div>
                        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors">
                            ← Hub
                        </Link>
                    </div>

                    {error && (
                        <div className="rounded-md border border-[hsl(350_60%_45%/0.3)] bg-[hsl(350_60%_18%/0.3)] p-3 text-sm text-[hsl(350_80%_72%)] mb-4 flex items-center gap-2">
                            <AlertTriangle size={14} /> {error}
                        </div>
                    )}

                    {feed.length === 0 && !feedLoading && (
                        <div className="glass rounded-2xl p-8 border border-dashed border-[hsl(40_40%_30%/0.3)] text-center" data-testid="feed-empty">
                            <Award size={28} className="mx-auto text-[hsl(45_85%_68%)] mb-3" />
                            <div className="font-heading text-xl">The void is quiet right now.</div>
                            <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                                No deliveries match your badges yet. Add more badges to widen your feed, or hold position — Mission Control transmits new deliveries daily.
                            </p>
                            <button
                                onClick={() => setBadgesOpen(true)}
                                data-testid="feed-empty-add-badges"
                                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                            >
                                <Award size={14} /> Manage Badges
                            </button>
                        </div>
                    )}

                    <div data-testid="feed-list">
                        {feed.map((o) => (
                            <StrikeCard key={o.id} order={o} badges={queue.badges} onAccept={accept} accepting={accepting} isDirector={false} />
                        ))}
                    </div>

                    {hasMore && (
                        <div ref={sentinelRef} className="py-8 text-center text-sm text-muted-foreground" data-testid="feed-sentinel">
                            {feedLoading ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 size={14} className="animate-spin" /> Pulling next strikes...
                                </span>
                            ) : (
                                <span className="opacity-60">Scroll for more</span>
                            )}
                        </div>
                    )}

                    {!hasMore && feed.length > 0 && (
                        <div className="py-8 text-center text-xs uppercase tracking-[0.3em] text-muted-foreground" data-testid="feed-end">
                            <Clock size={12} className="inline mr-2" /> End of feed · check back soon
                        </div>
                    )}
                </section>
                </div>
            </main>

            <BadgeManager
                open={badgesOpen}
                badges={queue.badges}
                available={queue.available_badges}
                onClose={() => setBadgesOpen(false)}
                onSave={saveBadges}
            />
        </div>
    );
}
